import React, {forwardRef, useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Button,
    Flex,
    List,
    ListItem,
    useMultiStyleConfig,
    useOutsideClick,
} from '@chakra-ui/react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

import {defineMessages, useIntl} from 'react-intl'
import InputComponent from '../field/input-component'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {remCalc} from '../../theme/_utils'
import {
    callLoqateCaptureService,
    callLoqateRetrieveService,
    defaultLoqateObject,
} from '../../services/loqate'
import SanitizedHtml from '../html-sanitizer'

const messages = defineMessages({
    addressLookup: {defaultMessage: 'Address', id: 'use_address_fields.label.addressLookup'},
    addressLookupHintText: {
        defaultMessage: 'Enter full address or postcode.',
        id: 'global.components.lookupComponent.label.addressLookupHintText',
    },
    addressLookupError: {
        defaultMessage:
            'Address cannot be found. Please try a different address or add your address manually.',
        id: 'global.components.lookupComponent.label.addressLookupError',
    },
})

const AddressType = {
    Container: 'Street',
    Address: 'Address',
    Unknown: 'Unknown',
}

const MIN_CHAR = 3

export function getCustomInputValue(defaultValue, value, options) {
    const mappedValue =
        typeof value !== 'string'
            ? value
            : typeof defaultValue === 'object'
            ? defaultValue
            : options && Array?.isArray(options)
            ? options.find((o) => o.value === defaultValue)
            : null

    return mappedValue !== null && mappedValue !== undefined && typeof mappedValue === 'object'
        ? Object.keys(mappedValue).length === 0
            ? null
            : mappedValue
        : mappedValue
}

const LoqateComponent = forwardRef(
    (
        {
            hintText,
            startConfig$,
            countryCode,
            onAddressUpdate,
            toggleAutocomplete = noop,
            limit = 10,
            isInvalid$,
            isBillingAddress = false,
            lookupAddressObject,
            setAutocompleteAvailable,
            rules,
            hideManualEntry = false,
            error,
            onBlur: externalOnBlur,
            onChange: externalOnChange,
        },
        ref,
    ) => {
        const {customParams} = getConfig()
        const {loqateApiKey} = customParams
        const [isFocused, setIsFocused] = useState(false)
        const [isOpen, setIsOpen] = useState(false)
        const [isInvalid, setInvalid] = useState(isInvalid$)
        const [loqateResponse, setLoqateResponse] = useState([])
        const [model, setModel] = useState({
            value:
                (lookupAddressObject?.Label && lookupAddressObject.Label.replace(/\n/g, ' ')) ||
                undefined,
            type: undefined,
            id: undefined,
        })
        const styles = useMultiStyleConfig('Field', {variant: isInvalid ? 'invalid' : 'default'})
        const {formatMessage} = useIntl()
        const _componentRef = useRef()
        const _lookupContainerElementRef = useRef()

        const startConfig = {
            title: startConfig$?.title || formatMessage(messages.addressLookup),
            hintText: hintText || formatMessage(messages.addressLookupHintText),
            errorMsg: startConfig$?.errorMsg || formatMessage(messages.addressLookupError),
        }

        const lookups = loqateResponse?.Items || []
        const lookupLength = lookups.length
        const countries = countryCode === 'FR' && isBillingAddress ? 'FR,MC' : countryCode

        useEffect(() => {
            setInvalid(isInvalid$)
        }, [isInvalid$])

        // check Loqate service availability on landing
        useEffect(() => {
            ;(async () => {
                const loqateResponse = await callLoqateCaptureService(
                    defaultLoqateObject({
                        Key: loqateApiKey,
                        Text: 'test',
                        Countries: countries,
                        Limit: limit,
                    }),
                )
                const errorCode = parseInt(loqateResponse?.Items?.[0]?.Error)
                const isServiceUnavailable =
                    (errorCode >= 400 && errorCode < 600) ||
                    errorCode > 1008 ||
                    !!loqateResponse?.error

                setAutocompleteAvailable(!isServiceUnavailable)
            })()

            function handleClickOutside(e) {
                if (_componentRef.current && !_componentRef.current.contains(e.target)) {
                    setIsOpen(false)
                }
            }

            function handleTabOut(e) {
                if (e.key === 'Tab') {
                    setTimeout(() => {
                        const active = document.activeElement
                        if (_componentRef.current && !_componentRef.current.contains(active)) {
                            setIsOpen(false)
                        }
                    }, 0)
                }
            }

            document.addEventListener('mousedown', handleClickOutside)
            document.addEventListener('keydown', handleTabOut)

            return () => {
                document.removeEventListener('mousedown', handleClickOutside)
                document.removeEventListener('keydown', handleTabOut)
            }
        }, [])

        // set address validity based on Loqate's response
        useEffect(() => {
            const errorCode = parseInt(loqateResponse?.Items?.[0]?.Error)
            if (errorCode || !!loqateResponse?.error) {
                setInvalid(true)
                setAutocompleteAvailable(false)
            }
            // console.log(loqateResponse?.Items?.[0]?.Error, errorCode)
            // ;(errorCode > 1000 && errorCode <= 1008 && setInvalid(true)) || !!loqateResponse?.error
        }, [loqateResponse])

        useEffect(() => {
            const value = model.value
            const get = setTimeout(() => {
                ;(value && value.length > MIN_CHAR && getDataAddress()) || setLoqateResponse([])
            }, 300)
            return () => clearTimeout(get)
        }, [model])

        const handleAddressUpdate = (obj = undefined) => {
            onAddressUpdate && onAddressUpdate(obj)
        }

        const handleChange = async ($event) => {
            const value = $event.target.value
            setInvalid(false)
            if (model.value !== value.replace(/\(|\)/g, '')) {
                handleAddressUpdate()
                setModel({id: undefined, type: undefined, value})
                if (externalOnChange) {
                    externalOnChange(value)
                }
            }
            setIsOpen(true)
        }

        const handleBlur = (e) => {
            if (!model.value && rules?.required) {
                setInvalid(true)
            }
            setIsFocused(false)
            if (externalOnBlur) {
                externalOnBlur(e)
            }
        }

        const getDataAddress = async () => {
            const addressLookupType = model?.type
            if (!addressLookupType) {
                const data = await callLoqateCaptureService(
                    defaultLoqateObject({
                        Key: loqateApiKey,
                        Text: model.value,
                        Countries: countries,
                        Limit: limit,
                    }),
                )
                setLoqateResponse({...data})
                if (data?.error || data?.Items.length === 0) setInvalid(true)
            } else if (addressLookupType !== AddressType.Address) {
                setLoqateResponse({
                    ...(await callLoqateCaptureService({
                        Key: loqateApiKey,
                        Text: model.value,
                        Container: model.id,
                    })),
                })
            } else if (addressLookupType === AddressType.Address) {
                const resp = await callLoqateRetrieveService({Key: loqateApiKey, Id: model.id})
                const item = resp.Items[0]
                // Keep the clicked suggestion text: Retrieve omits the street for PO-box-like
                // addresses (e.g. Packstation), see getLoqatePOBoxStreet
                handleAddressUpdate({...item, SuggestionText: model.value})
                setIsFocused(false)
            }
            _lookupContainerElementRef.current && (_lookupContainerElementRef.current.scrollTop = 0)
        }

        const getAddressLabel = (addressesLabel) => {
            return addressesLabel.includes('Addresses')
                ? `${addressesLabel.substr(0, addressesLabel.indexOf(' - '))}`
                : addressesLabel
        }

        const getCount = (addressesLabel) => {
            return addressesLabel.includes('Addresses')
                ? `${addressesLabel.substr(addressesLabel.indexOf(' - ') + 1)}`
                : ''
        }

        const getText = (initialText, addressesLabel) => {
            const values = model.value?.split(' ')
            let result = initialText
            values?.length > 0 &&
                values.map(
                    (v) =>
                        v &&
                        (result = result?.replace(
                            new RegExp(`(\\b${v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b)`, 'gi'), // Cleaning up v from special characters that could throw an error
                            `<span>$1</span>`,
                        )),
                )

            const formattedAddressesLabel = getAddressLabel(addressesLabel)
            const countLabel = getCount(addressesLabel)
            const numberCount = countLabel ? countLabel.match(/(\d+)/)?.[0] || 0 : 0
            const arrow = numberCount > 1 ? '<i class="icon-chevron" />' : ''

            return `<span class='line-address'><span><strong>${result}</strong>
            ${formattedAddressesLabel && formattedAddressesLabel} ${
                countLabel && countLabel
            }</span>${arrow}<span></span>`
        }

        const onLookupSelect = (selectedLookup) => {
            setModel({
                type: selectedLookup.Type,
                id: selectedLookup.Id,
                value: selectedLookup.Text,
            })
        }

        useOutsideClick({
            ref: _componentRef,
            handler: () => setIsFocused(false),
        })

        const [activeIndex, setActiveIndex] = useState(-1)
        const itemRefs = useRef([])

        useEffect(() => {
            if (lookupLength > 0 && activeIndex === -1) {
                setActiveIndex(0)
                setTimeout(() => itemRefs.current[0]?.focus(), 0)
            }
            if (lookupLength > 0 && activeIndex === -1) {
                setActiveIndex(0)
            }
            if (lookupLength === 0 && activeIndex !== -1) {
                setActiveIndex(-1)
            }
        }, [lookupLength])

        return (
            <Box w="100%" position="relative">
                <Box ref={_componentRef} {...styles.lookupContainer}>
                    <InputComponent
                        aria-label="address-lookup"
                        rules={rules}
                        placeholder={startConfig.title}
                        label={startConfig.title}
                        type="text"
                        ref={ref}
                        fillText={model.value}
                        value={model.value}
                        onFocus={() => setIsFocused(true)}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={isInvalid || !!error}
                        variant={
                            isFocused && _lookupContainerElementRef.current
                                ? 'lookupFocused'
                                : model.value
                                ? 'lookup'
                                : 'lookupEmpty'
                        }
                        sx={{
                            ...styles.lookupInput,
                        }}
                    />

                    <Box {...styles.lookupContainerAbs}>
                        {lookupLength > 0 && isOpen && (
                            <Box
                                ref={_lookupContainerElementRef}
                                {...styles.lookupInnerContainer}
                                sx={styles.lookupScrollbar}
                            >
                                <List
                                    role="listbox"
                                    {...styles.lookupHighlight}
                                    __css={{
                                        '.line-address': {
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            paddingRight: remCalc(18),
                                        },
                                        '.icon-chevron': {
                                            backgroundImage: `url(${getAssetUrl(
                                                'static/img/chevron.webp',
                                            )})`,
                                            width: remCalc(6),
                                            backgroundRepeat: 'no-repeat',
                                        },
                                    }}
                                >
                                    {lookups.map((l, index) => (
                                        <ListItem
                                            role="option"
                                            tabIndex={0}
                                            aria-label={`lookup-item ${index}`}
                                            onClick={() => onLookupSelect(l)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault()
                                                    onLookupSelect(l)
                                                }
                                            }}
                                            key={l.Id}
                                            {...styles.lookupItem}
                                        >
                                            <SanitizedHtml
                                                as={Box}
                                                html={getText(l.Text, l.Description)}
                                                sanitize={true}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        )}
                    </Box>
                </Box>
                {!hideManualEntry && (
                    <Flex {...styles.manualAddressBtnContainer}>
                        <Button
                            variant="link-button-no-underline"
                            size="small"
                            data-testid="manual-address-btn"
                            onClick={toggleAutocomplete}
                        >
                            {formatMessage({
                                defaultMessage: 'Add address manually',
                                id: 'global.component.addressLookup.addAddressManually',
                            })}
                        </Button>
                    </Flex>
                )}
            </Box>
        )
    },
)

LoqateComponent.displayName = 'Loqate Component'
LoqateComponent.propTypes = {
    countryCode: PropTypes.string,
    startConfig$: PropTypes.object,
    hintText: PropTypes.string,
    limit: PropTypes.number,
    onAddressUpdate: PropTypes.func,
    toggleAutocomplete: PropTypes.func,
    setAutocompleteAvailable: PropTypes.func,
    elementsCount: PropTypes.number,
    isInvalid$: PropTypes.bool,
    isBillingAddress: PropTypes.bool,
    rules: PropTypes.object,
    lookupAddressObject: PropTypes.object,
    hideManualEntry: PropTypes.bool,
    error: PropTypes.shape({
        message: PropTypes.string,
    }),
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
}

export default LoqateComponent
